import { useEffect } from 'react';
import { useEditor } from '../state/store';
import { toolByShortcut } from '../tools/registry';

/**
 * Global keyboard handling.
 *
 * Shortcuts are accelerators, never the only way to do something — every
 * binding here has a visible button or menu equivalent. Typing into a field
 * always wins, and the text tool consumes plain keys while it has a caret.
 */
export function useShortcuts(openDialog: (id: string) => void, openCommands: () => void): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable === true;
      if (typing) return;

      const s = useEditor.getState();
      const mod = e.ctrlKey || e.metaKey;

      // Text tool: plain keys type into the document.
      if (s.tool === 'text' && s.caret && !mod) {
        if (e.key === 'Escape') {
          s.setCaret(null);
          e.preventDefault();
          return;
        }
        if (e.key === 'Backspace') {
          s.caretBackspace();
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter') {
          s.caretNewline();
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
          s.setCaret({ x: Math.max(0, s.caret.x + d[0]), y: Math.max(0, s.caret.y + d[1]) });
          e.preventDefault();
          return;
        }
        if ([...e.key].length === 1) {
          s.typeText(e.key);
          e.preventDefault();
          return;
        }
      }

      if (mod && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        openCommands();
        return;
      }
      if (mod) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) s.redo();
            else s.undo();
            return;
          case 'y':
            e.preventDefault();
            s.redo();
            return;
          case 'a':
            e.preventDefault();
            s.selectAll();
            return;
          case 'c':
            e.preventDefault();
            s.copySelection();
            return;
          case 'x':
            e.preventDefault();
            s.cutSelection();
            return;
          case 'v':
            e.preventDefault();
            s.paste(s.cursor ?? undefined);
            return;
          case 'd':
            e.preventDefault();
            s.selectNone();
            return;
          case 'n':
            e.preventDefault();
            openDialog('new');
            return;
          case 'e':
            e.preventDefault();
            openDialog('export');
            return;
          case 'p':
            e.preventDefault();
            openDialog('preview');
            return;
          case 'f':
            e.preventDefault();
            openDialog('search');
            return;
          case 's':
            e.preventDefault();
            openDialog('save');
            return;
          case 'o':
            e.preventDefault();
            openDialog('open');
            return;
          case "'":
            e.preventDefault();
            s.setRenderOptions({ showGrid: !s.renderOptions.showGrid });
            return;
          case '=':
          case '+':
            e.preventDefault();
            s.setRenderOptions({ zoom: Math.min(6, s.renderOptions.zoom + 0.15) });
            return;
          case '-':
            e.preventDefault();
            s.setRenderOptions({ zoom: Math.max(0.25, s.renderOptions.zoom - 0.15) });
            return;
          case '0':
            e.preventDefault();
            s.setRenderOptions({ zoom: 1 });
            return;
          default:
            return;
        }
      }

      switch (e.key) {
        case 'Escape':
          s.cancelStroke();
          s.selectNone();
          s.setCaret(null);
          return;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          s.deleteSelection();
          return;
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowDown': {
          const d = {
            ArrowLeft: [-1, 0],
            ArrowRight: [1, 0],
            ArrowUp: [0, -1],
            ArrowDown: [0, 1],
          }[e.key];
          if (s.active().selection.isEmpty) return;
          e.preventDefault();
          s.nudgeSelection(d[0], d[1]);
          return;
        }
        case 'x':
        case 'X':
          if (!e.shiftKey) break;
          // Shift+X swaps the brush colours.
          e.preventDefault();
          s.setBrush({ fg: s.brush.bg, bg: s.brush.fg });
          return;
        case '[':
          e.preventDefault();
          s.setBrush({ size: Math.max(1, s.brush.size - 1) });
          return;
        case ']':
          e.preventDefault();
          s.setBrush({ size: Math.min(9, s.brush.size + 1) });
          return;
        default:
          break;
      }

      const tool = toolByShortcut(e.key);
      if (tool) {
        e.preventDefault();
        s.setTool(tool.id);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openDialog, openCommands]);
}
